<template>
  <div class="snackbar" :class="{ show, success, error, warning, info }">
    <div class="snackbar__icon">
      <i
        class="fas"
        :class="{
          'fa-info': info,
          'fa-exclamation': warning,
          'fa-times': error,
          'fa-check': success
        }"
      />
    </div>
    <div class="snackbar__text">
      <template v-if="hasDynamicValues">
        <i18n-t class="titleSmall" :keypath="title"></i18n-t>
        <TextSmall>
          <i18n-t tag="pre" :keypath="message">
            <template #workspaceLink>
              <router-link :to="{ name: 'clientOverview' }">
                {{ $t('shared.labels.workspace') }}
              </router-link>
            </template>
            <template
              v-for="option in options"
              :key="option.name"
              #[option.name]
            >
              <span>{{ option.value }}</span>
            </template>
          </i18n-t>
        </TextSmall>
      </template>
      <template v-else>
        <TitleSmall :title="title" />
        <TextSmall>
          <pre>{{ message }}</pre>
        </TextSmall>
      </template>
    </div>
    <button class="snackbar__close" @click="close">
      <i class="fas fa-times" />
    </button>
  </div>
</template>
<script setup>
import TextSmall from '@/components/shared/font/text/TextSmall'
import TitleSmall from '@/components/shared/font/title/TitleSmall'
import i18n from '@/i18n'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'

const store = useStore()
const $t = i18n.t

const time = ref(null)

const queue = computed(() => store.state.toast.queue)
const showToast = computed(() => store.state.toast.showToast)

const type = computed(() => {
  return queue.value[0] && queue.value[0].type
})
const title = computed(() => {
  return queue.value[0] && queue.value[0].title
})
const hasDynamicValues = computed(() => {
  return queue.value[0] && queue.value[0].hasDynamicValues
})
const message = computed(() => {
  return queue.value[0] && queue.value[0].message
})
const options = computed(() => {
  return queue.value[0] && queue.value[0].options
})
const show = computed(() => {
  return showToast.value
})
const success = computed(() => {
  return type.value === 'success'
})
const error = computed(() => {
  return type.value === 'error'
})
const warning = computed(() => {
  return type.value === 'warning'
})
const info = computed(() => {
  return type.value === 'info'
})

const currentTimeout = computed(() => {
  if (error.value) {
    return 10000
  } else {
    return 5000
  }
})

const closeToast = () => store.commit('toast/closeToast')
const openToast = () => store.commit('toast/openToast')

watch(
  () => queue.value.length,
  (length) => {
    if (length > 0) {
      setTimeout(() => {
        openToast()
      }, 500)
    }
  }
)

watch(
  () => show.value,
  (newValue) => {
    if (newValue) {
      time.value = setTimeout(() => {
        closeToast()
      }, currentTimeout.value)
    } else {
      clearTimeout(time.value)
    }
  }
)

function close() {
  clearTimeout(time.value)
  closeToast()
}
</script>
<style lang="scss" scoped>
.snackbar {
  visibility: hidden;
  min-width: 250px;
  background-color: $white;
  box-shadow: 0px 2px 5px $black-30;
  padding: 1rem;
  position: fixed;
  z-index: 100000;
  left: 50%;
  transform: translateX(-50%);
  top: 15px;
  border: 1px solid;
  border-radius: 5px;
  max-width: 800px;
  display: flex;

  @include mobileLandscape {
    min-width: 500px;
  }

  &__icon {
    width: 100%;
    max-width: 50px;
    height: 50px;
    margin-right: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 5px;

    .fas {
      font-size: 1.5rem;
    }
  }
  &__text {
    .titleSmall {
      margin-bottom: 0.5rem !important;
    }
  }
  &__close {
    all: unset;
    position: absolute;
    top: 10px;
    right: 10px;
    color: $black-50;
    cursor: pointer;
    :hover {
      color: $black-80;
    }
  }
  &.success {
    border-color: $green-100;
    color: $green-100;

    .titleSmall {
      color: $green-100;
    }
    .snackbar__icon {
      background: $green-20;
    }
  }
  &.error {
    border-color: $red-100;
    color: $red-100;

    .titleSmall {
      color: $red-100;
    }
    .snackbar__icon {
      background: $red-10;
    }
  }
  &.warning {
    border-color: $yellow-100;
    color: $yellow-100;

    .titleSmall {
      color: $yellow-100;
    }
    .snackbar__icon {
      background: $yellow-10;
    }
  }
  &.info {
    border-color: $blue-80;
    color: $blue-80;

    .titleSmall {
      color: $blue-80;
    }
    .snackbar__icon {
      background: $blue-10;
    }
  }

  &.show {
    visibility: visible; /* Show the snackbar */
    -webkit-animation:
      fadein 0.5s,
      fadeout 0.5s 9.5s;
    animation:
      fadein 0.5s,
      fadeout 0.5s 9.5s;
  }
  // Animate the snackbar's transition from visibility states and position:
  @-webkit-keyframes fadein {
    from {
      top: -15px;
      opacity: 0;
    }
    to {
      top: 15px;
      opacity: 1;
    }
  }

  @keyframes fadein {
    from {
      top: -15px;
      opacity: 0;
    }
    to {
      top: 15px;
      opacity: 1;
    }
  }

  @-webkit-keyframes fadeout {
    from {
      top: 15px;
      opacity: 1;
    }
    to {
      top: -15px;
      opacity: 0;
    }
  }

  @keyframes fadeout {
    from {
      top: 15px;
      opacity: 1;
    }
    to {
      top: -15px;
      opacity: 0;
    }
  }
}
pre {
  font-family: 'Poppins', sans-serif !important;
}
</style>
